import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canWrite } from '../../rbac';
import { contactMessageAPI } from '../../services/api';
import useBackButtonClose from '../../hooks/useBackButtonClose';
import { phoneHref } from '../../utils/phone';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './AdminContactMessages.css';

const STATUS_LABELS = {
  new: { label: 'नवीन', pillClass: 'new' },
  read: { label: 'वाचलेले', pillClass: 'read' },
  resolved: { label: 'निराकरण झाले', pillClass: 'resolved' },
  archived: { label: 'संग्रहित', pillClass: 'archived' },
};

export default function AdminContactMessages() {
  const { tenant, user } = useAuth();
  const slug = tenant?.slug;
  const canUpdate = canWrite(user?.role, 'content', user);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [updatingUid, setUpdatingUid] = useState(null);

  useBackButtonClose(Boolean(selectedMessage), () => setSelectedMessage(null));

  const loadMessages = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError('');
    try {
      const res = await contactMessageAPI.getAll(slug, { limit: 100 });
      setMessages(res.data.messages || []);
    } catch (err) {
      setError(err.response?.data?.error || 'संदेश लोड करता आले नाहीत. कृपया पुन्हा प्रयत्न करा.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const handleUpdateStatus = async (messageUid, newStatus) => {
    if (!slug || !canUpdate) return;
    setUpdatingUid(messageUid);
    setError('');
    try {
      await contactMessageAPI.updateStatus(slug, messageUid, newStatus);
      setMessages(prev =>
        prev.map(m =>
          m.message_uid === messageUid
            ? {
                ...m,
                status: newStatus,
                read_at: newStatus !== 'new' ? (m.read_at || new Date().toISOString()) : null,
                resolved_at: newStatus === 'resolved' ? new Date().toISOString() : m.resolved_at,
              }
            : m
        )
      );
      if (selectedMessage && selectedMessage.message_uid === messageUid) {
        setSelectedMessage(prev => ({
          ...prev,
          status: newStatus,
          read_at: newStatus !== 'new' ? (prev.read_at || new Date().toISOString()) : null,
          resolved_at: newStatus === 'resolved' ? new Date().toISOString() : prev.resolved_at,
        }));
      }
      setSuccess(`संदेश स्थिती यशस्वीरीत्या अद्ययावत केली: ${STATUS_LABELS[newStatus]?.label || newStatus}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'स्थिती अद्ययावत करता आली नाही.');
    } finally {
      setUpdatingUid(null);
    }
  };

  const counts = useMemo(() => {
    const total = messages.length;
    const newCount = messages.filter(m => m.status === 'new').length;
    const readCount = messages.filter(m => m.status === 'read').length;
    const resolvedCount = messages.filter(m => m.status === 'resolved').length;
    const archivedCount = messages.filter(m => m.status === 'archived').length;
    return { total, new: newCount, read: readCount, resolved: resolvedCount, archived: archivedCount };
  }, [messages]);

  const filteredMessages = useMemo(() => {
    return messages.filter(m => {
      if (filterStatus !== 'all' && m.status !== filterStatus) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = m.sender_name?.toLowerCase().includes(q);
        const matchesPhone = m.phone?.includes(q);
        const matchesEmail = m.email?.toLowerCase().includes(q);
        const matchesMsg = m.message?.toLowerCase().includes(q);
        return matchesName || matchesPhone || matchesEmail || matchesMsg;
      }
      return true;
    });
  }, [messages, filterStatus, searchQuery]);

  const formatDate = dateStr => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('mr-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const getInitials = name => {
    if (!name) return '👤';
    const parts = name.trim().split(' ');
    if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <div className="contact-msgs-page">
      {/* Header */}
      <div className="contact-msgs-header">
        <div>
          <h1>✉️ नागरिक संपर्क संदेश</h1>
          <p>नागरिकांकडून 'संपर्क' पृष्ठावरून पाठवलेले सर्व संदेश व चौकशी येथे व्यवस्थापित करा.</p>
        </div>
        <button
          type="button"
          className="btn btn-outline"
          onClick={loadMessages}
          disabled={loading}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          🔄 रीफ्रेश करा
        </button>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* KPI Cards */}
      <div className="contact-kpi-grid">
        <div
          className={`contact-kpi-card ${filterStatus === 'all' ? 'active' : ''}`}
          onClick={() => setFilterStatus('all')}
        >
          <div className="contact-kpi-icon all">📬</div>
          <div className="contact-kpi-body">
            <div className="contact-kpi-val">{counts.total}</div>
            <div className="contact-kpi-lbl">एकूण संदेश</div>
          </div>
        </div>

        <div
          className={`contact-kpi-card ${filterStatus === 'new' ? 'active' : ''}`}
          onClick={() => setFilterStatus('new')}
        >
          <div className="contact-kpi-icon new">✉️</div>
          <div className="contact-kpi-body">
            <div className="contact-kpi-val">{counts.new}</div>
            <div className="contact-kpi-lbl">नवीन (Unread)</div>
          </div>
        </div>

        <div
          className={`contact-kpi-card ${filterStatus === 'read' ? 'active' : ''}`}
          onClick={() => setFilterStatus('read')}
        >
          <div className="contact-kpi-icon read">👁️</div>
          <div className="contact-kpi-body">
            <div className="contact-kpi-val">{counts.read}</div>
            <div className="contact-kpi-lbl">वाचलेले</div>
          </div>
        </div>

        <div
          className={`contact-kpi-card ${filterStatus === 'resolved' ? 'active' : ''}`}
          onClick={() => setFilterStatus('resolved')}
        >
          <div className="contact-kpi-icon resolved">✅</div>
          <div className="contact-kpi-body">
            <div className="contact-kpi-val">{counts.resolved}</div>
            <div className="contact-kpi-lbl">निराकरण झाले</div>
          </div>
        </div>

        <div
          className={`contact-kpi-card ${filterStatus === 'archived' ? 'active' : ''}`}
          onClick={() => setFilterStatus('archived')}
        >
          <div className="contact-kpi-icon archived">📁</div>
          <div className="contact-kpi-body">
            <div className="contact-kpi-val">{counts.archived}</div>
            <div className="contact-kpi-lbl">संग्रहित</div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="contact-controls">
        <div className="contact-search-box">
          <span>🔍</span>
          <input
            type="text"
            placeholder="नाव, मोबाईल नंबर, ईमेल किंवा संदेशानुसार शोधा..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}
            >
              ✕
            </button>
          )}
        </div>

        <div className="contact-filter-tabs">
          <button
            type="button"
            className={`contact-filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            सर्व ({counts.total})
          </button>
          <button
            type="button"
            className={`contact-filter-btn ${filterStatus === 'new' ? 'active' : ''}`}
            onClick={() => setFilterStatus('new')}
          >
            नवीन ({counts.new})
          </button>
          <button
            type="button"
            className={`contact-filter-btn ${filterStatus === 'read' ? 'active' : ''}`}
            onClick={() => setFilterStatus('read')}
          >
            वाचलेले ({counts.read})
          </button>
          <button
            type="button"
            className={`contact-filter-btn ${filterStatus === 'resolved' ? 'active' : ''}`}
            onClick={() => setFilterStatus('resolved')}
          >
            निराकरण झाले ({counts.resolved})
          </button>
          <button
            type="button"
            className={`contact-filter-btn ${filterStatus === 'archived' ? 'active' : ''}`}
            onClick={() => setFilterStatus('archived')}
          >
            संग्रहित ({counts.archived})
          </button>
        </div>
      </div>

      {/* Messages List */}
      {loading ? (
        <LoadingSpinner fullPage message="संदेश लोड होत आहेत..." />
      ) : filteredMessages.length === 0 ? (
        <div className="contact-empty-state">
          <span>📭</span>
          <h3>कोणतेही संदेश सापडले नाहीत.</h3>
          <p>
            {searchQuery
              ? 'आपल्या शोध निकषांशी जुळणारे संदेश नाहीत.'
              : filterStatus !== 'all'
              ? `या स्थितीत (${STATUS_LABELS[filterStatus]?.label}) कोणतेही संदेश नाहीत.`
              : 'नागरिकांकडून अद्याप संपर्क संदेश प्राप्त झालेले नाहीत.'}
          </p>
        </div>
      ) : (
        <div className="contact-msg-list">
          {filteredMessages.map(msg => {
            const statusConfig = STATUS_LABELS[msg.status] || { label: msg.status, pillClass: 'archived' };
            const cleanPhone = msg.phone ? msg.phone.replace(/\D/g, '').slice(-10) : '';
            const waUrl = cleanPhone
              ? `https://wa.me/91${cleanPhone}?text=${encodeURIComponent(
                  `नमस्कार ${msg.sender_name || 'नागरिक'}, ग्रामपंचायत कार्यालयाशी संपर्क साधल्याबद्दल धन्यवाद.`
                )}`
              : null;

            return (
              <div
                key={msg.message_uid || msg.id}
                className={`contact-msg-card status-${msg.status || 'new'}`}
              >
                {/* Card Top */}
                <div className="contact-msg-top">
                  <div className="contact-msg-sender">
                    <div className="contact-avatar">{getInitials(msg.sender_name)}</div>
                    <div className="contact-sender-info">
                      <h3>
                        {msg.sender_name || 'अज्ञात नागरिक'}
                        {msg.status === 'new' && (
                          <span
                            style={{
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: '#2563eb',
                            }}
                          />
                        )}
                      </h3>
                      <div className="contact-sender-meta">
                        {msg.phone && (
                          <a href={phoneHref(msg.phone)} className="contact-meta-link">
                            📞 {msg.phone}
                          </a>
                        )}
                        {msg.email && (
                          <a href={`mailto:${msg.email}`} className="contact-meta-link">
                            ✉️ {msg.email}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="contact-msg-badges">
                    <span className="contact-time-badge">🕒 {formatDate(msg.created_at)}</span>
                    <span className={`contact-status-pill ${statusConfig.pillClass}`}>
                      {statusConfig.label}
                    </span>
                  </div>
                </div>

                {/* Message Preview */}
                <div
                  className="contact-msg-content"
                  onClick={() => setSelectedMessage(msg)}
                  style={{ cursor: 'pointer' }}
                  title="पूर्ण संदेश पाहण्यासाठी क्लिक करा"
                >
                  {msg.message}
                </div>

                {/* Actions */}
                <div className="contact-msg-actions">
                  <div className="contact-citizen-connect">
                    <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>
                      नागरिकाशी संपर्क:
                    </span>
                    {cleanPhone && (
                      <a
                        href={waUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-connect whatsapp"
                        title="व्हॉट्सॲपवर संदेश पाठवा"
                      >
                        💬 WhatsApp
                      </a>
                    )}
                    {msg.phone && (
                      <a href={phoneHref(msg.phone)} className="btn-connect phone" title="थेट कॉल करा">
                        📞 कॉल
                      </a>
                    )}
                    {msg.email && (
                      <a
                        href={`mailto:${msg.email}?subject=${encodeURIComponent(
                          'ग्रामपंचायत कार्यालय: आपला संपर्क संदेश संदर्भ'
                        )}`}
                        className="btn-connect email"
                        title="ईमेल पाठवा"
                      >
                        ✉️ ईमेल
                      </a>
                    )}
                    <button
                      type="button"
                      className="btn-connect"
                      style={{ background: '#e2e8f0', color: '#334155' }}
                      onClick={() => setSelectedMessage(msg)}
                    >
                      🔍 पूर्ण पाहा
                    </button>
                  </div>

                  {canUpdate && (
                    <div className="contact-status-actions">
                      {msg.status === 'new' && (
                        <button
                          type="button"
                          className="btn-status-act"
                          disabled={updatingUid === msg.message_uid}
                          onClick={() => handleUpdateStatus(msg.message_uid, 'read')}
                        >
                          👁️ वाचले
                        </button>
                      )}
                      {msg.status !== 'resolved' && (
                        <button
                          type="button"
                          className="btn-status-act resolve"
                          disabled={updatingUid === msg.message_uid}
                          onClick={() => handleUpdateStatus(msg.message_uid, 'resolved')}
                        >
                          ✅ निराकरण झाले
                        </button>
                      )}
                      {msg.status !== 'archived' && (
                        <button
                          type="button"
                          className="btn-status-act archive"
                          disabled={updatingUid === msg.message_uid}
                          onClick={() => handleUpdateStatus(msg.message_uid, 'archived')}
                        >
                          📁 संग्रहित
                        </button>
                      )}
                      {msg.status !== 'new' && (
                        <button
                          type="button"
                          className="btn-status-act"
                          disabled={updatingUid === msg.message_uid}
                          onClick={() => handleUpdateStatus(msg.message_uid, 'new')}
                        >
                          ↩️ नवीन करा
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full Detail Modal */}
      {selectedMessage && (
        <div className="contact-detail-modal-backdrop" onClick={() => setSelectedMessage(null)}>
          <div className="contact-detail-modal-box" onClick={e => e.stopPropagation()}>
            <div className="contact-modal-head">
              <h3>✉️ संपर्क संदेश तपशील</h3>
              <button
                type="button"
                className="contact-modal-close"
                onClick={() => setSelectedMessage(null)}
              >
                ✕
              </button>
            </div>

            <div className="contact-modal-content">
              <div className="contact-modal-meta-grid">
                <div className="contact-modal-meta-item">
                  <label>प्रेषकाचे नाव</label>
                  <strong>{selectedMessage.sender_name || '—'}</strong>
                </div>
                <div className="contact-modal-meta-item">
                  <label>सध्याची स्थिती</label>
                  <span
                    className={`contact-status-pill ${
                      STATUS_LABELS[selectedMessage.status]?.pillClass || 'archived'
                    }`}
                  >
                    {STATUS_LABELS[selectedMessage.status]?.label || selectedMessage.status}
                  </span>
                </div>
                <div className="contact-modal-meta-item">
                  <label>मोबाईल नंबर</label>
                  <strong>
                    {selectedMessage.phone ? (
                      <a href={phoneHref(selectedMessage.phone)}>{selectedMessage.phone}</a>
                    ) : (
                      '—'
                    )}
                  </strong>
                </div>
                <div className="contact-modal-meta-item">
                  <label>ईमेल पत्ता</label>
                  <strong>
                    {selectedMessage.email ? (
                      <a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a>
                    ) : (
                      '—'
                    )}
                  </strong>
                </div>
                <div className="contact-modal-meta-item">
                  <label>प्राप्त दिनांक व वेळ</label>
                  <strong>{formatDate(selectedMessage.created_at)}</strong>
                </div>
                {selectedMessage.read_at && (
                  <div className="contact-modal-meta-item">
                    <label>वाचल्याची तारीख</label>
                    <strong>{formatDate(selectedMessage.read_at)}</strong>
                  </div>
                )}
                {selectedMessage.resolved_at && (
                  <div className="contact-modal-meta-item">
                    <label>निराकरण तारीख</label>
                    <strong>{formatDate(selectedMessage.resolved_at)}</strong>
                  </div>
                )}
              </div>

              <div>
                <label
                  style={{
                    fontSize: '0.78rem',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    color: '#64748b',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  नागरिकाचा संपूर्ण संदेश:
                </label>
                <div className="contact-modal-msg-body">{selectedMessage.message}</div>
              </div>
            </div>

            <div className="contact-modal-foot">
              <div className="contact-citizen-connect">
                {selectedMessage.phone && (
                  <a
                    href={`https://wa.me/91${selectedMessage.phone.replace(/\D/g, '').slice(-10)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-connect whatsapp"
                  >
                    💬 WhatsApp
                  </a>
                )}
                {selectedMessage.phone && (
                  <a href={phoneHref(selectedMessage.phone)} className="btn-connect phone">
                    📞 कॉल
                  </a>
                )}
                {selectedMessage.email && (
                  <a href={`mailto:${selectedMessage.email}`} className="btn-connect email">
                    ✉️ ईमेल
                  </a>
                )}
              </div>

              {canUpdate && (
                <div className="contact-status-actions">
                  {selectedMessage.status === 'new' && (
                    <button
                      type="button"
                      className="btn-status-act"
                      disabled={updatingUid === selectedMessage.message_uid}
                      onClick={() => handleUpdateStatus(selectedMessage.message_uid, 'read')}
                    >
                      👁️ वाचले म्हणून चिन्हांकित करा
                    </button>
                  )}
                  {selectedMessage.status !== 'resolved' && (
                    <button
                      type="button"
                      className="btn-status-act resolve"
                      disabled={updatingUid === selectedMessage.message_uid}
                      onClick={() => handleUpdateStatus(selectedMessage.message_uid, 'resolved')}
                    >
                      ✅ निराकरण झाले
                    </button>
                  )}
                  {selectedMessage.status !== 'archived' && (
                    <button
                      type="button"
                      className="btn-status-act archive"
                      disabled={updatingUid === selectedMessage.message_uid}
                      onClick={() => handleUpdateStatus(selectedMessage.message_uid, 'archived')}
                    >
                      📁 संग्रहित करा
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
